import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PiContextType {
  user: any;
  isAuthenticated: boolean;
  piBalance: number;
  login: () => Promise<void>;
  logout: () => void;
  makePayment: (amount: number, memo: string) => Promise<any>;
  isInitialized: boolean;
}

const PiContext = createContext<PiContextType | undefined>(undefined);

export const usePi = () => {
  const context = useContext(PiContext);
  if (!context) {
    throw new Error('usePi must be used within a PiProvider');
  }
  return context;
};

interface PiProviderProps {
  children: ReactNode;
}

export const PiProvider: React.FC<PiProviderProps> = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [piBalance, setPiBalance] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if Pi SDK is available and initialized immediately
    const checkPiSDK = () => {
      if (typeof window !== 'undefined' && (window as any).Pi && (window as any).Pi._initialized) {
        console.log('Pi SDK is available and initialized');
        setIsInitialized(true);
        
        // Check if user is already authenticated
        const savedUser = localStorage.getItem('piDatingUser');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            setPiBalance(Math.random() * 1000); // Mock balance
          } catch (error) {
            console.error('Error parsing saved user:', error);
          }
        }
        return true;
      }
      return false;
    };

    // Try immediate check
    if (checkPiSDK()) {
      return;
    }

    // If not ready, wait for window load event
    const handleWindowLoad = () => {
      if (checkPiSDK()) {
        return;
      }
      
      // If still not ready after window load, use polling with cleanup
      const pollInterval = setInterval(() => {
        if (checkPiSDK()) {
          clearInterval(pollInterval);
        }
      }, 500);

      // Cleanup after 10 seconds to prevent infinite polling
      const cleanup = () => {
        clearInterval(pollInterval);
      };
      
      const timeoutId = window.setTimeout(cleanup, 10000);
      
      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeoutId);
      };
    };

    if (document.readyState === 'complete') {
      return handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
      return () => {
        window.removeEventListener('load', handleWindowLoad);
      };
    }
  }, []);

  const login = async () => {
    try {
      if ((window as any).Pi && isInitialized) {
        console.log('Attempting Pi authentication...');
        
        const scopes = ['username', 'payments'];
        const authResult = await (window as any).Pi.authenticate(scopes, onIncompletePaymentFound);
        
        console.log('Pi authentication successful:', authResult);
        
        setUser(authResult.user);
        setIsAuthenticated(true);
        
        // Save user to localStorage
        localStorage.setItem('piDatingUser', JSON.stringify(authResult.user));
        
        // Mock Pi balance (in real app, you'd fetch this from Pi Network)
        setPiBalance(Math.random() * 1000);
        
        return authResult;
      } else {
        throw new Error('Pi SDK not initialized. Please ensure you have the Pi Browser or Pi Network app.');
      }
    } catch (error) {
      console.error('Pi authentication failed:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('Logging out user...');
    setUser(null);
    setIsAuthenticated(false);
    setPiBalance(0);
    localStorage.removeItem('piDatingUser');
    localStorage.removeItem('piDatingTransactions');
    
    // Clear any other app-specific data
    localStorage.removeItem('piDatingNotificationSettings');
    localStorage.removeItem('piDatingTheme');
    localStorage.removeItem('piDatingLanguage');
    
    console.log('User logged out successfully');
  };

  const makePayment = async (amount: number, memo: string) => {
    try {
      if ((window as any).Pi && isInitialized) {
        console.log('Creating Pi payment...', { amount, memo });
        
        const paymentData = {
          amount: amount,
          memo: memo,
          metadata: { 
            productId: 'premium_subscription',
            userId: user?.uid || 'anonymous',
            timestamp: new Date().toISOString(),
            paymentMethod: 'pi_network'
          }
        };
        
        const payment = (window as any).Pi.createPayment(paymentData, {
          onReadyForServerApproval: (paymentId: string) => {
            console.log('Pi Payment ready for approval:', paymentId);
            
            // Save transaction to localStorage
            const transaction = {
              id: paymentId,
              type: 'buy',
              amount: amount,
              currency: 'π',
              paymentMethod: 'Pi Network',
              paymentMethodIcon: '🥧',
              description: memo,
              status: 'pending',
              date: new Date().toISOString(),
              feeOption: 'sender_pays',
              fee: 0
            };
            
            const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
            existingTransactions.unshift(transaction);
            localStorage.setItem('piDatingTransactions', JSON.stringify(existingTransactions));
          },
          
          onReadyForServerCompletion: (paymentId: string, txid: string) => {
            console.log('Pi Payment completed:', paymentId, txid);
            
            // Update transaction status
            const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
            const updatedTransactions = existingTransactions.map((t: any) => 
              t.id === paymentId ? { ...t, status: 'completed', txid } : t
            );
            localStorage.setItem('piDatingTransactions', JSON.stringify(updatedTransactions));
          },
          
          onCancel: (paymentId: string) => {
            console.log('Pi Payment cancelled:', paymentId);
            
            // Update transaction status
            const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
            const updatedTransactions = existingTransactions.map((t: any) => 
              t.id === paymentId ? { ...t, status: 'cancelled' } : t
            );
            localStorage.setItem('piDatingTransactions', JSON.stringify(updatedTransactions));
          },
          
          onError: (error: any, payment: any) => {
            console.error('Pi Payment error:', error);
            
            if (payment?.identifier) {
              // Update transaction status
              const existingTransactions = JSON.parse(localStorage.getItem('piDatingTransactions') || '[]');
              const updatedTransactions = existingTransactions.map((t: any) => 
                t.id === payment.identifier ? { ...t, status: 'failed', error: error.message } : t
              );
              localStorage.setItem('piDatingTransactions', JSON.stringify(updatedTransactions));
            }
          }
        });
        
        return payment;
      } else {
        throw new Error('Pi SDK not available. Please ensure you have the Pi Browser or Pi Network app installed.');
      }
    } catch (error) {
      console.error('Pi payment failed:', error);
      throw error;
    }
  };

  const onIncompletePaymentFound = (payment: any) => {
    console.log('Incomplete payment found:', payment);
    // Handle incomplete payments here
    // You might want to show a notification to the user
  };

  return (
    <PiContext.Provider value={{
      user,
      isAuthenticated,
      piBalance,
      login,
      logout,
      makePayment,
      isInitialized
    }}>
      {children}
    </PiContext.Provider>
  );
};